import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SunderlandCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sunderland Pothole Claims | 23% of U-Roads in RED | Fixtyer"
        description="Sunderland City Council earns GREEN on spend but AMBER overall — 23% of its 961km of residential roads are in RED condition, up from 18% in 2020, with 51,132 potholes filled in five years. See what that means for your claim."
        canonical="/councils/sunderland"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Sunderland', url: '/councils/sunderland' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Sunderland has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Sunderland projects £11.47m capital spend against a £6.63m DfT allocation in 2025/26 and targets 65.5% preventative maintenance. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Sunderland\'s own condition data shows unclassified RED condition rising from 18% to 23% while B/C roads in RED climbed back to 1.3% in 2024/25.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 961.1km — 82% of Sunderland\'s 1,168km carriageway network. The council surveys only around 25% of U-roads each year on a four-year cycle using coarse visual inspections. At the latest survey, 23% of U-roads were in RED condition — roughly 221km of estate roads, village routes and residential streets. That is your strongest structural argument: the council cannot claim detailed knowledge of a network it only inspects a quarter of each year.',
          },
          {
            question: 'Does the Vaisala AI survey change in 2024/25 affect my claim?',
            answer: 'It cuts both ways. Sunderland\'s report states that 2024 U-road results used the Vaisala system — a windscreen-mounted mobile phone with AI — and that "This change in methodology may explain any variations in the results." The jump from 19% to 23% RED may partly reflect a new ruler, not a sudden collapse. But the council\'s own footnote admits the figures may not be directly comparable — which weakens any Section 58 defence that relies on precise network knowledge.',
          },
          {
            question: 'Sunderland expects to fill 12,800 potholes in 2025/26 — does that mean the roads are fixed?',
            answer: 'No. Sunderland filled 11,422 potholes in 2024/25 and 51,132 over five years — yet U-road RED condition rose from 18% to 23% in the same period. Reactive volume and worsening survey data coexist. The council itself notes reactive spend is "influenced by the condition of the network and what is deemed actionable" while its statutory duty to maintain a safe highway remains the overriding priority.',
          },
          {
            question: 'Why is Sunderland AMBER on best practice despite digital innovation claims?',
            answer: 'The DfT Best Practice scorecard is AMBER — not GREEN — even though Sunderland highlights Vaisala AI surveys, ~80 VivaCity sensors, and North East Highway Alliance membership. Best practice ratings turn on published asset management evidence, PAS2161 transition readiness, and inspection coverage — and U-roads are still on a four-year coarse visual cycle covering only 25% per year. Innovation announcements do not automatically translate into DfT scorecard green.',
          },
          {
            question: 'How does Section 58 apply to Sunderland\'s risk-based inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Sunderland follows the Well-Managed Highway Infrastructure Code with a risk-based approach to repairs. Your claim succeeds when evidence shows the specific defect met intervention criteria, was previously reported, or should have been found before you hit it — especially on U-roads where three-quarters of the network goes uninspected in any given year.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Sunderland City Council"
        councilSlug="sunderland"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/sunderland"
        headline="Sunderland Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Sunderland</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Sunderland: 23% of Residential Roads Now in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Sunderland City Council projects capital spend 73% above its DfT allocation and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">23% of unclassified roads are now in RED condition</strong>, up from
            18% in 2020/21, on a network where 82% of carriageways are residential U-roads surveyed just once
            every four years.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">23%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 18% in 2020/21 — roughly 221km of residential streets on a 961km unclassified network
              where only 25% is inspected each year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            An 82% Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            Sunderland's own transparency report — network size by road class
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey cycle</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">94.9</td>
                  <td className="px-6 py-4 text-center">8.1%</td>
                  <td className="px-6 py-4 text-center text-emerald-700">100% annually (SCANNER)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B &amp; C roads</td>
                  <td className="px-6 py-4 text-center font-mono">112.2</td>
                  <td className="px-6 py-4 text-center">9.6%</td>
                  <td className="px-6 py-4 text-center">~50% per year (2-year cycle)</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">961.1</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-800">82.3%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">~25% per year (4-year cycle)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageways</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">1,168.2</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">1,709</div>
              <div className="text-sm text-surface-600 mt-1">km of footways maintained</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">427</div>
              <div className="text-sm text-surface-600 mt-1">bridges, footbridges, subways and retaining walls</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">44,700</div>
              <div className="text-sm text-surface-600 mt-1">highway gullies on the network</div>
            </div>
          </div>

          <p className="text-surface-600 text-sm mt-6">
            National Highways maintains the A1(M), A194(M) and A19 within the city boundary. Everything else
            in the table is Sunderland City Council's responsibility.
          </p>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data — classified roads stable, residential network worsening
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6 bg-white">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (94.9km — 8% of network): improving</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">1.2%</td>
                    <td className="px-4 py-2 text-center">13.7%</td>
                    <td className="px-4 py-2 text-center">85.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">1.0%</td>
                    <td className="px-4 py-2 text-center">9.8%</td>
                    <td className="px-4 py-2 text-center">89.2%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">0.9%</td>
                    <td className="px-4 py-2 text-center font-bold">9.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">90.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads are in genuinely good shape — 90% green. But A-roads are less than one-tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (112.2km — 10% of network): slipping back</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">1.35%</td>
                    <td className="px-4 py-2 text-center">17.15%</td>
                    <td className="px-4 py-2 text-center">81.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center text-emerald-700">0.85%</td>
                    <td className="px-4 py-2 text-center">11.0%</td>
                    <td className="px-4 py-2 text-center">88.15%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">1.05%</td>
                    <td className="px-4 py-2 text-center">11.9%</td>
                    <td className="px-4 py-2 text-center">87.05%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">1.3%</td>
                    <td className="px-4 py-2 text-center font-bold">12.1%</td>
                    <td className="px-4 py-2 text-center font-bold">86.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C RED condition has nearly doubled from its 2022/23 low (0.85% → 1.3%), and amber roads are
              back above 12%. Only ~50% of B/C roads are surveyed each year.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (961.1km — 82% of network): worsening</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-rose-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-rose-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600 text-xl">23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Nearly one in four U-roads is in RED — maintenance required now. That is approximately 221km of
              residential streets. Sunderland publishes RED percentages only for U-roads, not amber or green
              breakdowns.
            </p>
          </div>

          <QuoteCard
            quote="Approximately 25% of the unclassified road network was surveyed using coarse visual inspections, with the goal of covering the entire network over a four-year cycle. Please note that the 2024 results were based on survey data collected using the Vaisala system which utilises a windscreen mounted mobile phone and AI technology. This change in methodology may explain any variations in the results."
            source="Sunderland City Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spend section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend — But Reactive Pressure Persists
          </h2>
          <p className="text-surface-600 mb-8">
            Sunderland invests well above its DfT allocation — yet over a third of spend is still reactive
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£5,826,000</td>
                  <td className="px-4 py-3 text-center">£7,992,168</td>
                  <td className="px-4 py-3 text-center">£727,650</td>
                  <td className="px-4 py-3 text-center">69.3%</td>
                  <td className="px-4 py-3 text-center">30.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£4,129,000</td>
                  <td className="px-4 py-3 text-center">£9,859,633</td>
                  <td className="px-4 py-3 text-center">£724,574</td>
                  <td className="px-4 py-3 text-center">67.7%</td>
                  <td className="px-4 py-3 text-center">32.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£4,129,000</td>
                  <td className="px-4 py-3 text-center">£5,726,225</td>
                  <td className="px-4 py-3 text-center">£775,669</td>
                  <td className="px-4 py-3 text-center">58.9%</td>
                  <td className="px-4 py-3 text-center text-rose-600">41.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£5,888,000</td>
                  <td className="px-4 py-3 text-center">£7,598,150</td>
                  <td className="px-4 py-3 text-center">£735,726</td>
                  <td className="px-4 py-3 text-center">71.0%</td>
                  <td className="px-4 py-3 text-center">29.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£4,732,919</td>
                  <td className="px-4 py-3 text-center">£7,342,021</td>
                  <td className="px-4 py-3 text-center">£735,357</td>
                  <td className="px-4 py-3 text-center">64.9%</td>
                  <td className="px-4 py-3 text-center">35.1%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£6,631,120</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£11,472,000</td>
                  <td className="px-4 py-3 text-center">£735,022</td>
                  <td className="px-4 py-3 text-center font-bold">65.5%</td>
                  <td className="px-4 py-3 text-center">34.5%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Projected 2025/26 capital spend of £11.47m is 73% above the £6.63m DfT allocation. The council
                has also borrowed £19m in prudential borrowing over the last eight years specifically for highway
                maintenance, plus £9m for bridges.
              </p>
              <p className="text-emerald-800 text-sm">
                In 2024/25 the council resurfaced 15.54km and reconstructed 3.52km of carriageway. The 2025/26
                target is 19.3km of carriageway and 6.1km of footway.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Reactive Reality</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive maintenance — including pothole and footway repairs — still accounts for 29–41% of
                spend depending on the year. In 2022/23 reactive hit 41.1%, the highest in the five-year table.
              </p>
              <p className="text-amber-800 text-sm">
                Pothole repairs alone consumed 11.7–17.3% of reactive spend annually, costing £238,438 to
                £384,457 per year.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The proportion of spending on reactive maintenance is influenced by the condition of the network and what is deemed 'actionable'—that is, requiring urgent or emergency repair. Although efforts are made to minimise this expenditure, the Council's statutory duty to maintain a safe highway remains the overriding priority."
            source="Sunderland City Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Potholes filled */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            51,132 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a network where U-road RED condition keeps rising
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Repair cost</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of reactive spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">9,325</td>
                  <td className="px-6 py-4 text-center">£297,526</td>
                  <td className="px-6 py-4 text-center">17.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">8,455</td>
                  <td className="px-6 py-4 text-center">£292,710</td>
                  <td className="px-6 py-4 text-center">15.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">7,799</td>
                  <td className="px-6 py-4 text-center">£238,438</td>
                  <td className="px-6 py-4 text-center">11.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,131</td>
                  <td className="px-6 py-4 text-center">£358,658</td>
                  <td className="px-6 py-4 text-center">16.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">11,422</td>
                  <td className="px-6 py-4 text-center">£384,457</td>
                  <td className="px-6 py-4 text-center">14.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">51,132</td>
                  <td className="px-6 py-4 text-center font-bold">£1,571,789</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~28 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Sunderland fills around 28 potholes per day. The council expects
                approximately 12,800 repairs in 2025/26 based on recorded road condition — roughly 35 per day.
                A network producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The 2023/24 Spike</h3>
              <p className="text-rose-800 text-sm">
                Pothole fills jumped 81% year-on-year from 7,799 in 2022/23 to 14,131 in 2023/24 — the highest
                figure in the five-year table. That spike coincided with U-road RED condition holding at 19%.
                Reactive repairs did not prevent the jump to 23% RED in 2024/25.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection methodology & Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspection Gaps and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Sunderland surveys its roads — and what that means for your claim under the Highways Act
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Classified Roads: SCANNER</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A-roads: 100% surveyed annually via SCANNER laser vehicle</li>
                <li>• B/C roads: ~50% surveyed each year, full network over two years</li>
                <li>• RED means "should be considered for maintenance now"</li>
                <li>• From 2026/27, PAS2161 will replace the three-category system nationally</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">U-Roads: Coarse Visual + AI</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• ~25% of U-roads surveyed each year on a four-year cycle</li>
                <li>• Coarse visual inspections — not SCANNER laser surveys</li>
                <li>• 2024/25 data collected via Vaisala AI windscreen-mounted system</li>
                <li>• Council admits methodology change "may explain any variations"</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Sunderland Council follow the guidance set down in the 'Code of Practice on Well-Managed Highway Infrastructure' and use a risk-based approach when determining whether any repairs are required to the highway."
              source="Sunderland City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, a council must show it had a reasonable system for
              knowing the condition of its roads. For Sunderland's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it in the 25% inspected that year?</li>
              <li>• If 23% of U-roads are RED at the latest survey, what was done about yours specifically?</li>
              <li>• How does the council track deterioration on a road it may not inspect for four years?</li>
              <li>• Can pre- and post-2024 condition records be compared after the Vaisala methodology switch?</li>
              <li>• Does the risk-based Code approach mean your defect met the council's own intervention criteria?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of 961km of residential roads when it inspects only
              a quarter each year — and then changes how it measures.
            </p>
          </div>
        </div>
      </section>

      {/* Digital innovation angle */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Digital City, AMBER Best Practice
          </h2>
          <p className="text-surface-600 mb-8">
            Sunderland promotes AI surveys and smart sensors — the DfT best-practice scorecard is still AMBER
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The Council Highlights</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Vaisala AI mobile surveys for road condition assessment</li>
                <li>• ~80 VivaCity smart traffic sensors in and around the city centre</li>
                <li>• Founding member of the 11-authority North East Highway Alliance</li>
                <li>• Lead authority on the North East Underground Infrastructure Hub (NUAR prototype)</li>
                <li>• Chairs the NECA Highway Asset group</li>
                <li>• May 2025 Reclamite surface rejuvenation pilot and warm asphalt trials</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What The Data Shows Instead</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• DfT Best Practice rating: AMBER — not GREEN</li>
                <li>• U-road RED condition: 18% → 23% over five years</li>
                <li>• 82% of the network on a four-year coarse visual cycle</li>
                <li>• VivaCity sensors cover principal/major roads — not the 961km U-road network</li>
                <li>• ~25% of the 2025/26 highway programme delivered by September 2025 scrutiny meeting</li>
              </ul>
            </div>
          </div>

          <p className="text-surface-600 text-sm">
            Innovation and regional leadership are genuine — but they do not offset the structural inspection
            gap on residential roads where most pothole damage occurs.
          </p>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Sunderland invests seriously — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £11.47m projected capital vs £6.63m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £19m prudential borrowing for highways over eight years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving — 90% green, 0.9% red
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Well-Managed Highway Infrastructure Code compliance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Resilient network strategy aligned with DfT recommendations
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads and well-trafficked B/C routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED up from 18% to 23%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 82% of network on four-year survey cycle — only 25% inspected per year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 51,132 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits Vaisala methodology change may explain condition variations
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED nearly doubled from 2022/23 low — local roads slipping back
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented asset management, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the four-year survey gap is your strongest structural argument</li>
              <li>• Whether the defect met the council's own "actionable" threshold for urgent repair</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Sunderland's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Sunderland?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road four-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No Vaisala methodology challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise from 18% to 23% documented</li>
                <li>✅ Four-year survey gap argued</li>
                <li>✅ 51,132 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Sunderland</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=sunderland"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Sunderland Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sunderland has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Sunderland projects £11.47m capital spend against
                a £6.63m DfT allocation in 2025/26 and targets 65.5% preventative maintenance. Section 58 turns
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired —
                not on aggregate spend. Sunderland's own condition data shows unclassified RED condition rising
                from 18% to 23% while B/C roads in RED climbed back to 1.3% in 2024/25.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 961.1km — 82% of Sunderland's 1,168km carriageway network. The council surveys
                only around 25% of U-roads each year on a four-year cycle using coarse visual inspections. At the
                latest survey, 23% of U-roads were in RED condition — roughly 221km of estate roads, village routes
                and residential streets. That is your strongest structural argument: the council cannot claim detailed
                knowledge of a network it only inspects a quarter of each year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the Vaisala AI survey change in 2024/25 affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It cuts both ways. Sunderland's report states that 2024 U-road results used the Vaisala system — a
                windscreen-mounted mobile phone with AI — and that "This change in methodology may explain any
                variations in the results." The jump from 19% to 23% RED may partly reflect a new ruler, not a sudden
                collapse. But the council's own footnote admits the figures may not be directly comparable — which
                weakens any Section 58 defence that relies on precise network knowledge.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sunderland expects to fill 12,800 potholes in 2025/26 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Sunderland filled 11,422 potholes in 2024/25 and 51,132 over five years — yet U-road RED
                condition rose from 18% to 23% in the same period. Reactive volume and worsening survey data
                coexist. The council itself notes reactive spend is "influenced by the condition of the network and
                what is deemed actionable" while its statutory duty to maintain a safe highway remains the overriding
                priority.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Sunderland AMBER on best practice despite digital innovation claims?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is AMBER — not GREEN — even though Sunderland highlights Vaisala
                AI surveys, ~80 VivaCity sensors, and North East Highway Alliance membership. Best practice ratings
                turn on published asset management evidence, PAS2161 transition readiness, and inspection coverage
                — and U-roads are still on a four-year coarse visual cycle covering only 25% per year. Innovation
                announcements do not automatically translate into DfT scorecard green.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Sunderland's risk-based inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance
                systems. Sunderland follows the Well-Managed Highway Infrastructure Code with a risk-based approach
                to repairs. Your claim succeeds when evidence shows the specific defect met intervention criteria, was
                previously reported, or should have been found before you hit it — especially on U-roads where
                three-quarters of the network goes uninspected in any given year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.sunderland.gov.uk/article/35256/Local-Highway-Maintenance-Transparency-Report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Sunderland City Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
